import { useEffect, useRef, useState } from "react";
import { FiStar, FiAward, FiArrowUpRight } from "react-icons/fi";
import { motion } from "framer-motion";

/* ----------- Company data: fill `img` with the actual logo URL/path ----------- */
const COMPANIES = [
  { name: "ASBL", img: "https://assocham.org/assets/images/key-member-logos/1772791524_ASBL%20Logo.png", tag: "ASBL" },
  { name: "Asclepius", img: "https://assocham.org/assets/images/key-member-logos/1681299558_Asclepius.jpg", tag: "Asclepius" },
  { name: "asian", img: "https://assocham.org/assets/images/key-member-logos/1743155688_asian.jpg", tag: "asian" },
  { name: "attero", img: "https://assocham.org/assets/images/key-member-logos/1752063928_attero.jpg", tag: "attero" },
  { name: "Avaada", img: "https://assocham.org/assets/images/key-member-logos/1681299622_Avaada%20Logo.jpg", tag: "Avaada" },
  { name: "Axis", img: "https://assocham.org/assets/images/key-member-logos/1762148058_2.jpg", tag: "Axis" },
  { name: "Axis bank", img: "https://assocham.org/assets/images/key-member-logos/1778471819_Axis%20Bank.jpg", tag: "Axis bank" },
  { name: "Axis Max Life Insurance", img: "https://assocham.org/assets/images/key-member-logos/1782807248_Axis%20Max%20Life%20Insurance.png", tag: "Axis Max Life Insurance" },
  { name: "BAYER", img: "https://assocham.org/assets/images/key-member-logos/1762148069_3.jpg", tag: "BAYER" },
  { name: "BBK", img: "https://assocham.org/assets/images/key-member-logos/1762235316_2.jpg", tag: "BBK" },
  { name: "Bharat Pay", img: "https://assocham.org/assets/images/key-member-logos/1702364342_BharatPe%20Logo_page-0001.jpg", tag: "Bharat Pay" },
  { name: "Blossom Kochhar", img: "https://assocham.org/assets/images/key-member-logos/1681299889_Blossom%20Kochhar%20Logo.jpg", tag: "Blossom Kochhar" },
  { name: "BLS International", img: "https://assocham.org/assets/images/key-member-logos/1742904002_bls-logo.jpg", tag: "BLS International" },
  { name: "BLUME", img: "https://assocham.org/assets/images/key-member-logos/1773921142_Untitled%20design.jpg", tag: "BLUME" },
  { name: "BMW", img: "https://assocham.org/assets/images/key-member-logos/1681299917_bmw.jpg", tag: "BMW" },
  { name: "BOEING", img: "https://assocham.org/assets/images/key-member-logos/1762772882_1.jpg", tag: "BOEING" },
  { name: "BESE", img: "https://assocham.org/assets/images/key-member-logos/1734351096_BRPL.png", tag: "BESE" },
  { name: "Vedanta", img: "https://assocham.org/assets/images/key-member-logos/1762772983_3.jpg", tag: "Vedanta" },
  { name: "Canara HSBC Life Insurance", img: "https://assocham.org/assets/images/key-member-logos/1689681165_Canara%20HSBC%20Life%20Insurance%20Logo%20JPEG.jpg", tag: "Canara HSBC Life Insurance" },
  { name: "Capital Land", img: "https://assocham.org/assets/images/key-member-logos/1762148081_4.jpg", tag: "Capital Land" },
  { name: "Zupee", img: "https://assocham.org/assets/images/key-member-logos/1681300163_Cashgril.jpg", tag: "Zupee" },
  { name: "CATERPILLAR", img: "https://assocham.org/assets/images/key-member-logos/1681300184_Caterpillar-logo.jpg", tag: "CATERPILLAR" },
  { name: "CBRE", img: "https://assocham.org/assets/images/key-member-logos/1762148114_5.jpg", tag: "CBRE" },
  { name: "CUB", img: "https://assocham.org/assets/images/key-member-logos/1681300207_City%20Union%20Logo.jpg", tag: "CUB" },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: "easeOut" } 
  }
};

/* ---------------- Single animated logo card ---------------- */
function LogoCard({ company, index }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group relative flex-shrink-0 w-[170px] h-[110px] rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center p-5 cursor-pointer overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:border-[#DA2128]/40"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      {/* Animated sweep on hover */}
      <div
        className={`absolute inset-0 bg-gradient-to-r from-transparent via-[#FFF8F5] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 -skew-x-12`}
      />
      {/* Glow ring */}
      <div
        className={`absolute inset-0 rounded-2xl transition-all duration-500 ${
          hover ? "shadow-[0_0_30px_-5px_rgba(218,33,40,0.4)]" : ""
        }`}
      />

      {/* Full-size logo, fills the card */}
      <img
        src={company.img}
        alt={company.name}
        title={company.name}
        className="relative max-w-full max-h-full w-auto h-auto object-contain transition-all duration-500 group-hover:scale-110"
      />

      {/* Tag pill */}
      <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider bg-[#FFF8F5] text-[#DA2128] px-1.5 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
        {company.tag}
      </span>

      {/* Corner arrow */}
      <FiArrowUpRight className="absolute bottom-2 right-2 text-[#DA2128] opacity-0 group-hover:opacity-100 group-hover:translate-x-0 group-hover:-translate-y-0 translate-x-2 translate-y-2 transition-all duration-300" />
    </div>
  );
}

/* ---------------- Marquee row ---------------- */
function MarqueeRow({ items, reverse = false, speed = 40 }) {
  return (
    <div className="marquee-wrap py-2">
      <div
        className="flex gap-5 w-max"
        style={{
          animation: `${reverse ? "marqueeReverse" : "marquee"} ${speed}s linear infinite`,
        }}
      >
        {[...items, ...items].map((c, i) => (
          <LogoCard key={`${c.name}-${i}`} company={c} index={i} />
        ))}
      </div>
    </div>
  );
}

/* ---------------- Bento logo grid (visible on larger screens) ---------------- */
// function LogoBento() {
//   const top = COMPANIES.slice(0, 12);
//   return (
//     <div className="mt-14 hidden lg:block reveal">
//       <div className="flex items-center justify-center gap-4 mb-8">
//         <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#DA2128]/30" />
//         <div className="flex items-center gap-2 text-sm font-bold text-[#DA2128] uppercase tracking-widest">
//           <FiAward /> Our Patron Network
//         </div>
//         <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#DA2128]/30" />
//       </div>
//       <div className="grid grid-cols-6 gap-4">
//         {top.map((c, i) => (
//           <div
//             key={c.name}
//             className="reveal group relative aspect-[16/10] rounded-2xl bg-white border border-gray-100 shadow-sm flex items-center justify-center card-hover overflow-hidden p-4"
//             style={{ transitionDelay: `${i * 50}ms` }}
//           >
//             <img
//               src={c.img}
//               alt={c.name}
//               title={c.name}
//               className="max-w-full max-h-full w-auto h-auto object-contain transition-transform duration-500 group-hover:scale-110"
//             />
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }

/* ---------------- Main component ---------------- */
export default function KeyMembers() {
  const [count, setCount] = useState(0);
  const counterRef = useRef(null);

  // Counter animation on mount
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            let start = 0;
            const end = 450;
            const duration = 1600;
            const startTime = performance.now();
            const tick = (now) => {
              const p = Math.min((now - startTime) / duration, 1);
              setCount(Math.floor(p * end));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    if (counterRef.current) obs.observe(counterRef.current);
    return () => obs.disconnect();
  }, []);

  const row1 = COMPANIES.slice(0, 12);
  const row2 = COMPANIES.slice(12, 24);

  return (
    <section id="members" className="py-24 relative overflow-hidden bg-gradient-to-b from-[#FAF7F2] to-white">
      {/* Decorative blurs */}
      <div className="absolute top-20 -left-20 w-80 h-80 rounded-full bg-[#DA2128]/10 blur-3xl" />
      <div className="absolute bottom-20 -right-20 w-96 h-96 rounded-full bg-[#E54F15]/10 blur-3xl" />
      <div className="absolute inset-0 bg-dots opacity-40 pointer-events-none" style={{ backgroundSize: "28px 28px" }} />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10 reveal">
          {/* <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[#DA2128]/20 text-[#DA2128] text-xs font-bold uppercase tracking-widest mb-5 shadow-sm">
            <FiStar className="text-[#E54F15]" /> Key Members
          </div> */}
           <motion.h2 
              variants={fadeInUp}
              className="font-serif text-4xl font-medium leading-tight tracking-tight sm:text-3xl lg:text-4xl"
            >
              Key {" "}
              <motion.span 
                className="italic text-[#DA2128]"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                Member
              </motion.span>
            </motion.h2>
          {/* <h2 className="text-3xl md:text-5xl font-black text-gray-900 leading-tight">
            Trusted by India's <span className="text-grad">Leading Companies</span>
          </h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
            From Tata to Reliance, from PSUs to MNCs — our member network spans India's most iconic enterprises across every major sector.
          </p> */}

          {/* Animated counters */}
          {/* <div ref={counterRef} className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5 max-w-3xl mx-auto">
            {[
              { n: `${count}+`, l: "Elite Members", icon: "🏆" },
              { n: "24+", l: "Sectors", icon: "🏭" },
              { n: "100+", l: "Councils", icon: "🤝" },
              { n: "$3T+", l: "Combined M-Cap", icon: "📈" },
            ].map((s) => (
              <div
                key={s.l}
                className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm card-hover"
              >
                <div className="text-2xl mb-1">{s.icon}</div>
                <div className="text-2xl md:text-3xl font-black text-[#DA2128]">{s.n}</div>
                <div className="text-[11px] text-gray-500 uppercase tracking-widest font-bold">
                  {s.l}
                </div>
              </div>
            ))}
          </div> */}
        </div>

        {/* Marquee row 1 */}
        <MarqueeRow items={row1} speed={45} />
        {/* Marquee row 2 (reverse) */}
        <MarqueeRow items={row2} speed={55} reverse />
        {/* Marquee row 3 (slower, mixed) */}
        <MarqueeRow items={[...row2.slice(4), ...row1.slice(0, 8)]} speed={70} />

      

      </div>
    </section>
  );
}